import classNames from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../button/button";
import { Spiner } from "../spiner";

/**
 * Su tamaño se ajusta automáticamente en función del contenido, hasta llegar a dos lineas
 * @param {*} props 
 * @returns 
 */
export const TextAreaResize = (props) => {
    const { onChange, className, ...restProps } = props;
    const textareaRef = useRef(null);
    const [isWrapped, setIsWrapped] = useState(false);
    const handleChange = (e) => {
        onChange?.(e);
        const newText = e.target.value;
        if (textareaRef.current) {
            if (newText.includes('\n')) {
                setIsWrapped(true);
            } else {
                const wrapped = isTextWrapped(textareaRef.current);
                setIsWrapped(wrapped);
            }
        }
    };

    const isTextWrapped = (textarea) => {
        const textWidth = getTextWidth(textarea.value, textarea);
        const textareaWidth = textarea.offsetWidth;
        return textWidth > textareaWidth;
    }

    const getTextWidth = (text, textarea) => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        const style = window.getComputedStyle(textarea);
        const font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
        context.font = font;
        return context.measureText(text).width;
    }

    const _className = useMemo(() => {
        return classNames('h-10', { 'h-16': isWrapped }, className);
    }, [className, isWrapped]);

    return (
        <textarea
            ref={textareaRef}
            {...restProps}
            onChange={handleChange}
            className={_className}
        />)
}

export const AutoResizeTextarea = ({ value, onChange, className,focused,Options=null,...rest }) => {
    const textareaRef = useRef(null);
    const [lineHeight, setLineHeight] = useState(0); // Para calcular el alto por línea
    const [charsPerLine, setCharsPerLine] = useState(0); // Para calcular cuántos caracteres caben por línea
    // Calcular la cantidad de caracteres por línea según el ancho del textarea
    useEffect(() => {
        if (textareaRef.current) {
            const width = textareaRef.current.offsetWidth;
            const fontSize = parseFloat(window.getComputedStyle(textareaRef.current).fontSize);
            const charWidth = fontSize * 0.6; // Aproximación del ancho de cada carácter
            setCharsPerLine(Math.floor(width / charWidth));
            setLineHeight(parseFloat(window.getComputedStyle(textareaRef.current).lineHeight));
        }
    }, []);

    // Manejar el cambio de valor
    useEffect(() => {
        if(value) adjustHeight(value);
    // eslint-disable-next-line
    }, [value, charsPerLine, lineHeight]);

    const handleChange = (e) => {
        const inputValue = e.target.value;
        onChange(inputValue); // Llamamos a onChange para manejar el valor
    };

    const adjustHeight = (inputValue) => {
        const lines = Math.ceil(inputValue.length / charsPerLine); // Calcular el número de líneas
        let newHeight = lines * lineHeight; // Ajustar la altura basada en el número de líneas
        if (textareaRef.current) {
            newHeight = newHeight>=300?300:newHeight;
            textareaRef.current.style.height = `${newHeight}px`;
        }
    };

    const blur = () => {
        textareaRef.current.blur();
    }

    return (
        <div className={classNames(" border-none rounded-md w-full p-2 h-fit", className)}>
            <textarea
                {...rest}
                ref={textareaRef}
                value={value}
                onChange={handleChange}
                rows="1"
                className="w-full border-none resize-none  bg-transparent font-semibold outline-none"
                style={{ height: 'auto' }}
            />
            {Options?<Options {...{blur,focused}} />:null}
        </div>
    );
};

export const SendIcon = ({ className }) => {
    return (
        <svg className={classNames(``, className)} width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g>
                <path d="M17.0778 28.8284L24.1277 11.2002C24.4529 10.38 23.6468 9.57385 22.8266 9.89912L5.19841 16.949C4.52665 17.2106 4.35695 18.0733 4.859 18.5753L8.11169 21.8422C8.46524 22.1957 9.03092 22.2381 9.4269 21.927L20.7548 13.272L12.0927 24.5928C11.7886 24.9959 11.8311 25.5615 12.1846 25.9151L15.4515 29.1678C15.9535 29.6698 16.8162 29.5001 17.0778 28.8284Z" fill="#3D96C2" />
            </g>
            <defs>
                <clipPath id="clip0_2947_11950">
                    <rect width="24" height="24" fill="white" transform="translate(0.0585938 17) rotate(-45)" />
                </clipPath>
            </defs>
        </svg>
    );
}

export const SendIcon2 = () => {
    const { t } = useTranslation();
    return (<div className="text-[14px]">
        <SendIcon />
        <label>{t("send")}</label>
    </div>)
}

/**
 * Hook para el manejo de los mensajes
 * @param {*} param0 
 * @returns 
 */
export const useMessageInput = ({ sendMessage, value = "" }) => {
    const [message, setMessages] = useState(value);
    const [loading, setLoading] = useState(false);
    const handleChange = useCallback((e) => {
        setMessages(e.target.value);
    }, []);
    const clearMessage = useCallback(() => {
        setMessages("");
    }, []);
    const _sendMessage = useCallback(() => {
        setLoading(true);
        sendMessage(message, { clearMessage })
            ?.catch(() => { })
            ?.finally(() => {
                setLoading(false);
            });
    }, [message, sendMessage, clearMessage]);
    return {
        message,
        loading,
        handleChange,
        clearMessage,
        setLoading,
        sendMessage: _sendMessage,
        bind: {
            value: message,
            onChange: handleChange,
            sendMessage: _sendMessage,
            loading
        }
    }
}

/**
 * Componente para el input del los mensajes
 * @returns 
 */
export const MessageInput = ({ loading, value, onChange, sendMessage, keyplaceholder }) => {

    const { t } = useTranslation();
    return (

        <div className="flex flex-col gap-4 sm-max:gap-2 w-full">
            <div
                className={`w-full min-h-16 max-h-25 bg-DarkBlue-700/90 rounded-4 sm-max:rounded-4 py-2 px-4 flex flex-row gap-2 flex-grow items-center`}
            >
                <TextAreaResize
                    value={value}
                    onChange={onChange}
                    className="resize-none overflow-auto p-2 w-full border rounded-md bg-white/13 text-Grey-500 font-semibold rounded-1 shadow placeholder:text-Grey-800 w-full p-0 text-4"
                    placeholder={t(keyplaceholder)}
                />
                <div className="w-[70px] flex justify-center items-center">
                    {
                        loading ? <Spiner /> :
                            <Button
                                onClick={sendMessage}
                                typeB={'button'}
                                label={<SendIcon2 />}
                                className="!py-0 m-0"
                                variant="primary-text"
                            />
                    }
                </div>
            </div>
        </div>
    )
}




/**
 * Search Input permite realizar una busqueda, en tiempo real o por evento
 */

export const useSearchInput = ({ onSearch } = {}) => {
    const [value, setValue] = useState("");
    const handleChange = useCallback((e) => {
        setValue(e.target.value);
    }, []);
    const _onSearch = useCallback(() => {
        onSearch?.(value);
    }, [value, onSearch]);
    return {
        value,
        handleChange,
        onSearch: _onSearch,
        bind: {
            value,
            onChange: handleChange,
            onSearch: _onSearch
        }
    }
}

export const SearchInput = ({ value, onChange, onSearch, className }) => {
    const { t } = useTranslation();
    return (
        <div className={classNames('flex flex-row gap-2 items-center', className)}>
            <input
                type="text"
                value={value}
                onChange={onChange}
                placeholder={t("search")}
                className=" overflow-auto p-2 w-full border rounded-md border-[white]/20 border-[white]/20 !border-1 text-[white] bg-[white]/0 font-semibold w-full p-0 text-4 "

            />
            <label onClick={onSearch} className="-ml-12 text-12 text-[white]/20 cursor-pointer">⌕</label>
        </div>
    )
}